import React, { useEffect, useMemo, useRef } from "react";
import JsBarcode from "jsbarcode";
import { Modal } from "components/ui/Modal";
import { Button } from "components/ui/Button";

export const BarcodeModal = ({ open, onClose, item }) => {
  const svgRef = useRef(null);

  // Код для штрих-кода: SKU если есть, иначе ITM-000123
  const code = useMemo(() => {
    if (!item) return "";
    if (item.sku?.trim()) return item.sku.trim();
    const idPart = String(item.id ?? "").padStart(6, "0");
    return `ITM-${idPart}`;
  }, [item]);

  useEffect(() => {
    if (!open || !item || !svgRef.current) return;
    try {
      // Рендерим CODE128 (универсальный, принимает цифры/буквы)
      JsBarcode(svgRef.current, code, {
        format: "CODE128",
        width: 2,      // толщина штриха
        height: 80,    // высота штрихов
        displayValue: true,
        font: "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto",
        fontSize: 14,
        textMargin: 6,
        margin: 10,
      });
    } catch (e) {
      console.error("Barcode error:", e);
    }
  }, [open, item, code]);

  const doPrint = () => {
    if (!svgRef.current) return;
    const svgHtml = svgRef.current.outerHTML;
    const title = `${item?.name ?? "Товар"} — ${code}`;
    const win = window.open("", "_blank", "width=600,height=400");
    if (!win) return;

    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; margin: 0; padding: 16px; background: white; color: black; }
            .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; width: 420px; background: white; }
            .name { font-size: 14px; color: #111827; margin-bottom: 8px; font-weight: 600; }
            .meta { font-size: 12px; color: #6b7280; margin-bottom: 8px; }
            .bar { display: flex; align-items: center; justify-content: center; }
            @page { margin: 12mm; }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="name">${escapeHtml(item?.name ?? "")}</div>
            <div class="meta">ID: ${item?.id ?? "—"} · Код: ${code}</div>
            <div class="bar">${svgHtml}</div>
          </div>
          <script>window.onload = () => { window.print(); }</script>
        </body>
      </html>
    `);
    win.document.close();
  };

  if (!item) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Штрих-код товара"
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose} className="text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800">Закрыть</Button>
          <Button onClick={doPrint} className="bg-blue-600 hover:bg-blue-700 text-white">Печать</Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="text-sm text-slate-900 dark:text-slate-100">
          <div className="font-semibold">{item.name}</div>
          <div className="text-slate-600 dark:text-slate-400">ID: {item.id} · Код: {code}</div>
        </div>
        <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white shadow-sm flex items-center justify-center overflow-hidden">
          {/* JsBarcode рендерит сюда */}
          <svg ref={svgRef} className="max-w-full" />
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Формат: CODE128. Используется SKU, а если его нет — шаблон <b>ITM-000000</b> на основе ID.
        </div>
      </div>
    </Modal>
  );
};

// простая экранизация для печати
function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (m) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[m])
  );
}
